import { useEffect, useRef, useState } from "react";

export function Webcam(): JSX.Element {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let stream: MediaStream | null = null;

    async function start(): Promise<void> {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: "user" },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => { /* autoplay errors are fine */ });
          setActive(true);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    }

    void start();

    return () => {
      cancelled = true;
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  if (error) {
    return (
      <div className="bg-red-900/40 border border-red-700 rounded-2xl p-3 text-red-200 text-xs">
        Camera: {error}
      </div>
    );
  }

  return (
    <div className="relative rounded-2xl overflow-hidden border-2 border-ink-600 shadow-xl bg-ink-800">
      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        className="block w-full h-full object-cover scale-x-[-1]"
        aria-label="Your webcam"
      />
      {!active && (
        <div className="absolute inset-0 flex items-center justify-center text-ink-500 text-xs">
          Connecting camera…
        </div>
      )}
    </div>
  );
}
